import React, { useEffect, useMemo, useState } from "react";
import convert from "convert-units";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import Footer from "../components/Footer";
import { updateMetaTags } from "../utils/seo";
import {
  defaultPairs,
  measureMeta,
  measureOrder,
  presetPairs,
  type UnitMeasure,
} from "../data/unitConverter";

interface ListedUnit {
  abbr: string;
  measure: UnitMeasure;
  system: string;
  singular: string;
  plural: string;
}

const formatDisplayNumber = (value: number, precision: number) => {
  if (!Number.isFinite(value)) return "—";
  if (value !== 0 && (Math.abs(value) >= 1e9 || Math.abs(value) < 1e-6)) {
    return value.toExponential(Math.min(precision, 6));
  }

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: precision,
  }).format(value);
};

const converterFaqItems = [
  {
    question: "What units can I convert with this tool?",
    answer:
      "This unit converter supports length, area, mass, volume, temperature, time, speed, pressure, digital storage, energy, electrical measurements and many other categories.",
  },
  {
    question: "Is this unit converter free to use?",
    answer:
      "Yes. The converter runs in the browser and is free to use for everyday, technical and professional conversions.",
  },
  {
    question: "Can I use this converter on mobile devices?",
    answer:
      "Yes. The interface is designed to work on phones, tablets and desktop screens with fast search, clear result cards and responsive controls.",
  },
  {
    question: "Does this page support scientific and engineering units?",
    answer:
      "Yes. Along with common measurements like kilograms and kilometers, the page also supports electrical units, pressure, frequency, reactive power, apparent power, illuminance and more.",
  },
] as const;

const converterUseCases = [
  "Length, height and distance conversions for travel and construction",
  "Weight and mass conversions for fitness, shipping and commerce",
  "Temperature conversions for weather, cooking and lab work",
  "Digital storage conversions for files, data and bandwidth planning",
  "Electrical, energy and pressure conversions for technical work",
  "Quick everyday conversions on mobile, tablet and desktop",
];

const totalCategories = measureOrder.length;

const UnitConverter: React.FC = () => {
  const allUnits = useMemo(() => convert().list() as ListedUnit[], []);

  const unitsByMeasure = useMemo(
    () =>
      measureOrder.reduce(
        (accumulator, measure) => {
          accumulator[measure] = allUnits.filter(
            (unit) => unit.measure === measure,
          );
          return accumulator;
        },
        {} as Record<UnitMeasure, ListedUnit[]>,
      ),
    [allUnits],
  );

  const totalUnits = allUnits.length;
  const availableMeasures = measureOrder.filter(
    (measure) => unitsByMeasure[measure]?.length,
  );

  const [measure, setMeasure] = useState<UnitMeasure>("length");
  const [amount, setAmount] = useState(defaultPairs.length.amount);
  const [fromUnit, setFromUnit] = useState(defaultPairs.length.from);
  const [toUnit, setToUnit] = useState(defaultPairs.length.to);
  const [precision, setPrecision] = useState(4);
  const [unitExplorerQuery, setUnitExplorerQuery] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);

  const categoryUnits = useMemo(
    () => unitsByMeasure[measure] ?? [],
    [measure, unitsByMeasure],
  );

  const fromUnitOption =
    categoryUnits.find((unit) => unit.abbr === fromUnit) ?? null;
  const toUnitOption =
    categoryUnits.find((unit) => unit.abbr === toUnit) ?? null;

  const structuredData = useMemo(
    () =>
      JSON.stringify([
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Free Unit Converter",
          applicationCategory: "UtilitiesApplication",
          operatingSystem: "Any",
          url: `${window.location.origin}/unit-converter`,
          description:
            "Free online unit converter with length, weight, temperature, speed, digital storage, pressure, energy, electrical units and many more.",
          offers: {
            "@type": "Offer",
            price: "0",
            priceCurrency: "USD",
          },
          featureList: [
            "22 measurement categories",
            "100+ supported units",
            "Searchable unit picker",
            "Popular conversion presets",
            "Copyable conversion results",
            "Responsive mobile and desktop layout",
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: converterFaqItems.map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.shashi-sharma.com.np/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Utilities",
              item: "https://www.shashi-sharma.com.np/unit-converter",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Unit Converter",
              item: "https://www.shashi-sharma.com.np/unit-converter",
            },
          ],
        },
      ]),
    [],
  );

  useEffect(() => {
    updateMetaTags({
      title:
        "Free Unit Converter for Length, Weight, Temperature, Pressure, Data & More | Shashi Sharma",
      description:
        "Free online unit converter with 22 categories including length, area, weight, speed, temperature, digital storage, pressure, energy, angle and electrical units. Fast, responsive and easy to use.",
      keywords:
        "unit converter, free unit converter, online unit converter, weight converter, length converter, temperature converter, pressure converter, digital storage converter, speed converter, electrical unit converter",
      canonical: `${window.location.origin}/unit-converter`,
      ogTitle:
        "Free Unit Converter for Length, Weight, Temperature, Pressure, Data & More",
      ogDescription:
        "Convert across 22 categories and 100+ units with fast search, presets, reverse conversion and a responsive interface for mobile and desktop.",
      ogUrl: `${window.location.origin}/unit-converter`,
      ogType: "website",
      ogImage: `${window.location.origin}/favicon.png`,
      twitterCard: "summary",
      twitterTitle:
        "Free Unit Converter for Length, Weight, Temperature, Pressure, Data & More",
      twitterDescription:
        "Convert between length, mass, temperature, speed, pressure, data and many more units with fast search and copyable results.",
      twitterImage: `${window.location.origin}/favicon.png`,
      structuredData,
    });
  }, [structuredData]);

  useEffect(() => {
    const defaults = defaultPairs[measure];
    const currentUnits = unitsByMeasure[measure] ?? [];

    setAmount(defaults.amount);
    setUnitExplorerQuery("");

    if (!currentUnits.some((unit) => unit.abbr === fromUnit)) {
      setFromUnit(defaults.from);
    }

    if (!currentUnits.some((unit) => unit.abbr === toUnit)) {
      setToUnit(defaults.to);
    }
  }, [measure, fromUnit, toUnit, unitsByMeasure]);

  const amountValue = amount.trim() === "" ? Number.NaN : Number(amount);
  const amountError =
    amount.trim() === ""
      ? "Enter a value to convert."
      : Number.isNaN(amountValue)
        ? "Use a valid number for conversion."
        : "";

  const conversion = useMemo(() => {
    if (amountError || !fromUnit || !toUnit) {
      return null;
    }

    try {
      const converted = convert(amountValue)
        .from(fromUnit as never)
        .to(toUnit as never);
      const reverse = convert(1)
        .from(toUnit as never)
        .to(fromUnit as never);
      const best = convert(amountValue)
        .from(fromUnit as never)
        .toBest();

      return {
        converted,
        reverse,
        best,
      };
    } catch (_error) {
      return null;
    }
  }, [amountError, amountValue, fromUnit, toUnit]);

  const filteredExplorerUnits = useMemo(() => {
    const query = unitExplorerQuery.trim().toLowerCase();
    if (!query) {
      return categoryUnits;
    }

    return categoryUnits.filter((unit) => {
      const haystack = [unit.abbr, unit.singular, unit.plural, unit.system]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [categoryUnits, unitExplorerQuery]);

  const handleSwapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
  };

  const handleReset = () => {
    const defaults = defaultPairs[measure];
    setAmount(defaults.amount);
    setFromUnit(defaults.from);
    setToUnit(defaults.to);
    setPrecision(4);
    setUnitExplorerQuery("");
    setFeedback("Unit converter reset for this category.");
  };

  const handleCopy = async () => {
    if (!conversion) return;

    const textToCopy = `${formatDisplayNumber(amountValue, precision)} ${fromUnit} = ${formatDisplayNumber(
      conversion.converted,
      precision,
    )} ${toUnit}`;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setFeedback("Conversion copied.");
    } catch (_error) {
      setFeedback("Clipboard access is not available in this browser.");
    }
  };

  const activePresets = presetPairs[measure] ?? [];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, sm: 3.5 }, flex: 1 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              component="h1"
              variant="h4"
              sx={{ fontWeight: 800, mb: 1 }}
            >
              Free Unit Converter
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 960 }}
            >
              Convert between length, area, mass, temperature, pressure, speed,
              digital storage, electrical units and many more in one fast
              utility. Search units instantly, swap directions, apply presets
              and copy the result.
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1.5 }}
            >
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${totalCategories} categories`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`${totalUnits}+ units`}
              />
              <Chip size="small" variant="outlined" label="Mobile friendly" />
              <Chip size="small" variant="outlined" label="Fast search" />
            </Stack>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(320px, 380px) minmax(0, 1fr)",
              },
              gap: 2,
              alignItems: "start",
            }}
          >
            <Stack
              spacing={2}
              sx={{
                order: { xs: 1, sm: 1 },
                position: { sm: "sticky" },
                top: { sm: 88 },
              }}
            >
              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        Live result
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={measureMeta[measure].label}
                        />
                        <Chip size="small" variant="outlined" label="Instant" />
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        p: 2.25,
                        borderRadius: 3,
                        background:
                          "linear-gradient(180deg, rgba(145, 189, 58, 0.12) 0%, rgba(145, 189, 58, 0.04) 100%)",
                        border: "1px solid",
                        borderColor: "rgba(145, 189, 58, 0.2)",
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.75 }}
                      >
                        {fromUnitOption
                          ? `${formatDisplayNumber(amountValue, precision)} ${fromUnitOption.plural}`
                          : "Select units"}
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 800,
                          lineHeight: 1.1,
                          wordBreak: "break-word",
                          fontSize: { xs: "2rem", sm: "2.6rem" },
                        }}
                      >
                        {conversion
                          ? formatDisplayNumber(conversion.converted, precision)
                          : "—"}
                      </Typography>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                      >
                        {toUnitOption?.plural ?? ""}
                      </Typography>
                    </Box>

                    {amountError && (
                      <Alert severity="warning">{amountError}</Alert>
                    )}

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.25,
                      }}
                    >
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.default",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Reverse
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, fontWeight: 700 }}
                        >
                          {conversion
                            ? `1 ${toUnit} = ${formatDisplayNumber(conversion.reverse, precision)} ${fromUnit}`
                            : "Waiting for valid values"}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          backgroundColor: "background.default",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Best unit
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.5, fontWeight: 700 }}
                        >
                          {conversion
                            ? `${formatDisplayNumber(conversion.best.val, precision)} ${conversion.best.plural}`
                            : `${totalCategories} categories, ${totalUnits}+ units`}
                        </Typography>
                      </Box>
                    </Box>

                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1.25}
                    >
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopy}
                        disabled={!conversion}
                      >
                        Copy result
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<RestartAltIcon />}
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                  <Stack spacing={1.5}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Coverage
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {totalCategories} categories • {totalUnits}+ units •
                      metric, imperial, bytes, bits and technical measurement
                      systems.
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {availableMeasures.slice(0, 10).map((item) => (
                        <Chip
                          key={item}
                          size="small"
                          variant="outlined"
                          label={measureMeta[item].shortLabel}
                        />
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              {feedback && (
                <Alert severity="success" onClose={() => setFeedback(null)}>
                  {feedback}
                </Alert>
              )}
            </Stack>

            <Card variant="outlined" sx={{ order: { xs: 2, sm: 2 } }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, mb: 1.5 }}
                    >
                      Category
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {availableMeasures.map((item) => (
                        <Chip
                          key={item}
                          clickable
                          color={item === measure ? "primary" : "default"}
                          variant={item === measure ? "filled" : "outlined"}
                          label={measureMeta[item].label}
                          onClick={() => setMeasure(item)}
                        />
                      ))}
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1.25 }}
                    >
                      {measureMeta[measure].description}
                    </Typography>
                  </Box>

                  <Divider />

                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "minmax(0, 1fr) 160px",
                        },
                        gap: 1.5,
                      }}
                    >
                      <TextField
                        fullWidth
                        label="Amount"
                        value={amount}
                        onChange={(event) => setAmount(event.target.value)}
                        inputProps={{ inputMode: "decimal" }}
                        helperText="Enter any number, including decimals."
                      />

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ mb: 1, fontWeight: 700 }}
                        >
                          Precision: {precision}
                        </Typography>
                        <TextField
                          select
                          fullWidth
                          value={String(precision)}
                          onChange={(event) =>
                            setPrecision(Number(event.target.value))
                          }
                          size="small"
                        >
                          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((value) => (
                            <MenuItem key={value} value={value}>
                              {value} decimals
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "minmax(0, 1fr) 64px minmax(0, 1fr)",
                        },
                        gap: 1.5,
                        alignItems: "center",
                      }}
                    >
                      <Autocomplete
                        options={categoryUnits}
                        value={fromUnitOption}
                        onChange={(_event, value) =>
                          value && setFromUnit(value.abbr)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.abbr === value.abbr
                        }
                        getOptionLabel={(option) =>
                          `${option.singular} (${option.abbr})`
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="From" />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Stack>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {option.singular} ({option.abbr})
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.system}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      />

                      <Tooltip title="Swap from and to units">
                        <span>
                          <IconButton
                            onClick={handleSwapUnits}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 2,
                              width: 56,
                              height: 56,
                              mx: "auto",
                            }}
                            disabled={!fromUnit || !toUnit}
                          >
                            <SwapHorizIcon />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Autocomplete
                        options={categoryUnits}
                        value={toUnitOption}
                        onChange={(_event, value) =>
                          value && setToUnit(value.abbr)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.abbr === value.abbr
                        }
                        getOptionLabel={(option) =>
                          `${option.singular} (${option.abbr})`
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="To" />
                        )}
                        renderOption={(props, option) => (
                          <Box component="li" {...props}>
                            <Stack>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {option.singular} ({option.abbr})
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {option.system}
                              </Typography>
                            </Stack>
                          </Box>
                        )}
                      />
                    </Box>
                  </Stack>

                  {!!activePresets.length && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, mb: 1.25 }}
                      >
                        Popular conversions
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        {activePresets.map((preset) => (
                          <Chip
                            key={preset.label}
                            clickable
                            variant="outlined"
                            label={preset.label}
                            onClick={() => {
                              setAmount(preset.amount);
                              setFromUnit(preset.from);
                              setToUnit(preset.to);
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, mb: 1.25 }}
                    >
                      Explore all units in {measureMeta[measure].label}
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="Search units by name, abbreviation or system"
                      value={unitExplorerQuery}
                      onChange={(event) =>
                        setUnitExplorerQuery(event.target.value)
                      }
                      sx={{ mb: 1.5 }}
                    />
                    <Box
                      sx={{
                        maxHeight: 420,
                        overflowY: "auto",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 3,
                      }}
                    >
                      <Stack divider={<Divider flexItem />}>
                        {filteredExplorerUnits.map((unit) => (
                          <Box
                            key={unit.abbr}
                            sx={{
                              px: 2,
                              py: 1.5,
                              display: "flex",
                              gap: 1.5,
                              alignItems: { xs: "flex-start", sm: "center" },
                              justifyContent: "space-between",
                              flexDirection: { xs: "column", sm: "row" },
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  wordBreak: "break-word",
                                }}
                              >
                                {unit.singular} ({unit.abbr})
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {unit.system} • {unit.plural}
                              </Typography>
                            </Box>
                            <Stack
                              direction="row"
                              spacing={1}
                              sx={{ flexShrink: 0 }}
                            >
                              <Button
                                size="small"
                                variant={
                                  fromUnit === unit.abbr
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() => setFromUnit(unit.abbr)}
                              >
                                From
                              </Button>
                              <Button
                                size="small"
                                variant={
                                  toUnit === unit.abbr
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() => setToUnit(unit.abbr)}
                              >
                                To
                              </Button>
                            </Stack>
                          </Box>
                        ))}
                        {!filteredExplorerUnits.length && (
                          <Box sx={{ px: 2, py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              No units match this search.
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 2,
                    }}
                  >
                    <Card variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                          >
                            Why this is easy to use
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Search the exact unit you need, choose a preset if
                            it is common, then copy the final conversion with
                            one click.
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>

                    <Card variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800 }}
                          >
                            Coverage snapshot
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Includes everyday, scientific, digital and
                            electrical units in one utility so you do not need
                            multiple converter tools.
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box component="section">
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 1 }}
                    >
                      Unit converter for everyday, academic and technical use
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This free unit converter is designed for fast and accurate
                      conversions across common and advanced measurements. It
                      helps with everyday conversions like kilometers to miles
                      and kilograms to pounds, while also supporting technical
                      categories such as frequency, energy, reactive power,
                      pressure, voltage and digital storage.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Popular unit conversion use cases
                      </Typography>
                      <Stack spacing={1}>
                        {converterUseCases.map((item) => (
                          <Typography
                            key={item}
                            variant="body2"
                            color="text.secondary"
                          >
                            • {item}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography
                        component="h3"
                        variant="subtitle1"
                        sx={{ fontWeight: 700, mb: 1.25 }}
                      >
                        Why this unit converter is useful
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          • Covers both common global units and specialized
                          technical measurements.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Keeps the result highly visible while you change
                          categories or units.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Works well on phone, tablet and desktop screens with
                          responsive controls.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          • Makes large unit libraries practical through search,
                          presets and quick selection buttons.
                        </Typography>
                      </Stack>
                    </Box>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{ fontWeight: 800, mb: 2 }}
                    >
                      Frequently asked questions
                    </Typography>
                    <Stack spacing={2}>
                      {converterFaqItems.map((item) => (
                        <Box key={item.question}>
                          <Typography
                            component="h3"
                            variant="subtitle1"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                          >
                            {item.question}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.answer}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Stack>
      </Container>

      <Footer />
    </Box>
  );
};

export default UnitConverter;
